import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useDispatch } from "react-redux";

import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { CustomDateRange } from "src/app/components/CustomDateRange/CustomDateRange";
import { translateMessage } from "src/app/methods/translateMessage";
import { useIntl } from "react-intl";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import {
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import { clearActivityFilters, setActivityFilters } from "src/redux";
import { saveToLocalStorage } from "src/utils/localStorage";
import { initialActivityFilters } from "src/redux/activity/activityReducer";
import ActivityFiltersItem from "./ActivityFiltersItem";
import ActivityFiltersProjectSearchSelect from "./ActivityFiltersProjectSearchSelect";
import ActivityFiltersMemberSelect from "./ActivityFiltersMemberSelect";

interface ActivityFiltersProps {
  activeWorkspaceUuid: string;
  setFiltersVisible: Dispatch<SetStateAction<boolean>>;
  filtersApplied: boolean;
  filters: any; // TODO: change type
}

export function ActivityFilters({
  activeWorkspaceUuid,
  setFiltersVisible,
  filtersApplied,
  filters,
}: ActivityFiltersProps) {
  const [dateFrom, setDateFrom] = useState<string | null>(
    filters.dateRange.dateFrom,
  );
  const [dateTo, setDateTo] = useState<string | null>(filters.dateRange.dateTo);
  const [selectedProjectUuids, setSelectedProjectUuids] = useState<string[]>(
    filters.campaigns,
  );
  const [selectedMemberUuids, setSelectedMemberUuids] = useState(
    filters.members,
  );

  const intl = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!filtersApplied) return;

    const filtersToSave = {
      campaigns: selectedProjectUuids,
      members: selectedMemberUuids,
      dateRange: { dateFrom, dateTo },
    };

    dispatch(setActivityFilters(filtersToSave));

    let currentActivityFiltersObject = {
      [activeWorkspaceUuid]: filtersToSave,
    };

    const currentActivityFilters = localStorage.getItem("activityFilters");

    if (currentActivityFilters) {
      currentActivityFiltersObject = {
        ...JSON.parse(currentActivityFilters),
        [activeWorkspaceUuid]: filtersToSave,
      };
    }

    saveToLocalStorage("activityFilters", currentActivityFiltersObject);
  }, [dateFrom, dateTo, selectedMemberUuids, selectedProjectUuids]);

  useEffect(() => {
    if (initialActivityFilters === filters) {
      setDateFrom(null);
      setDateTo(null);
      setSelectedProjectUuids([]);
      setSelectedMemberUuids([]);
    }
  }, [filters]);

  const showClearButton =
    selectedProjectUuids?.length ||
    selectedMemberUuids.length ||
    dateFrom ||
    dateTo;

  return (
    <>
      <PaneTopbar>
        <PaneTopbarLeft>
          <FilterIcon className="filters-header__icon" />
          <span className="filters-header__title">
            <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
          </span>
        </PaneTopbarLeft>
        <PaneTopbarToolbar hidePane={() => setFiltersVisible(false)} />
      </PaneTopbar>

      <div className="pane__container filters activity-filters">
        <ActivityFiltersItem
          key="time"
          label={<IDHFormattedMessage id="ws_time" defaultMessage="Time" />}
        >
          <CustomDateRange
            dateFrom={dateFrom}
            setDateFrom={(date) => setDateFrom(date)}
            dateTo={dateTo}
            setDateTo={(date) => setDateTo(date)}
            maxDate={new Date()}
            minDate={new Date("01 Jan 2022 00:00:00 GMT")}
            errorMessage={translateMessage({
              intl,
              id: "ws_date_must_be_within_7_days",
              defaultMessage: "Date range must be within 7 days",
            })}
            toRight
          />
        </ActivityFiltersItem>

        <ActivityFiltersItem
          key="campaign"
          label={
            <IDHFormattedMessage id="ws_campaign" defaultMessage="Campaign" />
          }
        >
          <ActivityFiltersProjectSearchSelect
            activeWorkspaceUuid={activeWorkspaceUuid}
            selectedProjectUuids={selectedProjectUuids}
            setSelectedProjectUuids={setSelectedProjectUuids}
          />
        </ActivityFiltersItem>

        <ActivityFiltersItem
          key="member"
          label={
            <IDHFormattedMessage
              id="ws_member_client"
              defaultMessage="Member / Client"
            />
          }
        >
          <ActivityFiltersMemberSelect
            selectedMemberUuids={selectedMemberUuids}
            setSelectedMemberUuids={setSelectedMemberUuids}
          />
        </ActivityFiltersItem>
      </div>
      {showClearButton && (
        <Button
          variant="light"
          size="medium"
          className="filters-button"
          onClick={() => dispatch(clearActivityFilters())}
        >
          <IDHFormattedMessage
            id="ws_clear_filters"
            defaultMessage="Clear filters"
          />
        </Button>
      )}
    </>
  );
}
